import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap';

export type DismissGuard = () => boolean | Promise<boolean>;

/**
 * The channel a modal closes itself through. Provided under the component's own `resultToken`, so
 * the value it closes with is checked against the same type the opener reads back.
 */
export class ModalCloser<TResult> {
    private modalRef: NgbModalRef | undefined;
    private dismissGuard: DismissGuard | undefined;

    /**
     * Called by `ModalInputService` the moment `NgbModal.open()` returns. The closer has to exist
     * before that call, because it is provided into the injector the call consumes, so it cannot
     * capture the ref at construction.
     */
    public attach(modalRef: NgbModalRef): void {
        this.modalRef = modalRef;
    }

    /** The modal decides whether Escape, a backdrop click or the X may proceed. */
    public guardDismiss(dismissGuard: DismissGuard): void {
        this.dismissGuard = dismissGuard;
    }

    public mayDismiss(): boolean | Promise<boolean> {
        return this.dismissGuard?.() ?? true;
    }

    public close(result: TResult): void {
        this.opened().close(result);
    }

    public dismiss(reason?: unknown): void {
        this.opened().dismiss(reason);
    }

    private opened(): NgbModalRef {
        if (this.modalRef == null) {
            throw new Error(
                'The modal closed before its ref was attached. A modal cannot close itself while ' +
                    'it is still being constructed.',
            );
        }
        return this.modalRef;
    }
}
